import type { FabButton, FabButtonType } from '@/shared/types/fab-button';
import { parseLegacyId } from '@/shared/lib/legacy-id';
import { FAB_TYPE_META } from './fab-button-meta';

/** Documento FAB na legacy-api (Mongo em passbooks.fabs). */
export interface LegacyFab {
  _id?: unknown;
  label?: string;
  url?: string;
  backgroundColor?: string;
  foregroundColor?: string;
  launchDirect?: boolean;
  active?: boolean;
  order?: number;
  icon?: string;
  /** Tipo com comportamento próprio. 'mgm' = Indique e Ganhe. */
  kind?: string;
  /** O app anexa o CPF do cliente ao fim da url. */
  shouldShow?: boolean;
}

const FAB_COLUMNS = [
  { data: 'label', searchable: 'true' },
  { data: 'url', searchable: 'true' },
  { data: 'backgroundColor', searchable: 'true' },
  { data: 'foregroundColor', searchable: 'true' },
  { data: 'active', searchable: 'true' },
  { data: 'order', searchable: 'true' },
];

export function fabDataTableColumns() {
  return FAB_COLUMNS;
}

function inferTypeFromUrl(url: string): FabButtonType {
  const lower = url.toLowerCase();
  if (lower.includes('wa.me') || lower.includes('whatsapp')) return 'whatsapp';
  if (lower.startsWith('tel:')) return 'phone';
  if (lower.startsWith('mailto:')) return 'email';
  if (lower.includes('instagram.com') || lower.startsWith('@')) return 'instagram';
  if (lower.includes('google.com/maps') || lower.includes('maps.app')) return 'location';
  return 'link';
}

function extractValue(type: FabButtonType, url: string): string {
  if (!url) return '';
  switch (type) {
    case 'phone':
      return url.replace(/^tel:/i, '');
    case 'email':
      return url.replace(/^mailto:/i, '');
    case 'instagram':
      if (url.startsWith('@')) return url;
      try {
        const path = new URL(url.startsWith('http') ? url : `https://${url}`).pathname;
        const handle = path.split('/').filter(Boolean)[0];
        return handle ? `@${handle}` : url;
      } catch {
        return url;
      }
    default:
      return url;
  }
}

export function mapLegacyFab(row: LegacyFab): FabButton {
  const url = row.url ?? '';
  const type: FabButtonType = row.kind === 'mgm' ? 'mgm' : inferTypeFromUrl(url);
  return {
    _id: parseLegacyId(row._id),
    label: row.label ?? '',
    type,
    value: extractValue(type, url),
    color: row.backgroundColor ?? '#2563eb',
    order: row.order == null ? undefined : Number(row.order),
    active: row.active ?? true,
    iconUrl: row.icon,
    iconColor: row.foregroundColor,
    foregroundColor: row.foregroundColor,
    launchDirect: row.launchDirect,
  };
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export function valueToLegacyUrl(type: FabButtonType, value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  switch (type) {
    case 'whatsapp': {
      const digits = digitsOnly(trimmed);
      return digits ? `https://wa.me/${digits}` : trimmed;
    }
    case 'phone':
      return trimmed.startsWith('tel:') ? trimmed : `tel:${trimmed}`;
    case 'email':
      return trimmed.startsWith('mailto:') ? trimmed : `mailto:${trimmed}`;
    case 'instagram':
      if (trimmed.startsWith('http')) return trimmed;
      return `https://instagram.com/${trimmed.replace(/^@/, '')}`;
    case 'location':
    case 'link':
    case 'other':
    case 'mgm':
    default:
      return trimmed;
  }
}

export function mapToLegacyFabBody(payload: {
  label: string;
  type: FabButtonType;
  value: string;
  color?: string;
  iconColor?: string;
  active: boolean;
  launchDirect?: boolean;
  /** Tipo gravado antes da edição: sair de "Indique e ganhe" limpa kind/shouldShow. */
  previousType?: FabButtonType;
}) {
  const base = {
    label: payload.label,
    url: valueToLegacyUrl(payload.type, payload.value),
    backgroundColor: payload.color ?? '#2563eb',
    foregroundColor: payload.iconColor ?? '#ffffff',
    launchDirect: payload.launchDirect ?? true,
    active: payload.active,
  };
  if (payload.type === 'mgm') {
    // Abre dentro do app (webview) e com o CPF anexado ao fragment `#u=`.
    return { ...base, launchDirect: false, kind: 'mgm', shouldShow: true };
  }
  if (payload.previousType === 'mgm') return { ...base, kind: '', shouldShow: false };
  return base;
}

export function fabTypeLabel(type: FabButtonType): string {
  return FAB_TYPE_META[type].label;
}
